import React, { useState } from "react";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { subscribeNewsletter } from "@/lib/api";
import { home } from "@/config/site";

export default function NewsletterForm({ compact = false }) {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !email.trim()) {
      toast.error("Please add your name and email.");
      return;
    }
    setLoading(true);
    try {
      const res = await subscribeNewsletter({ first_name: firstName.trim(), email: email.trim() });
      setDone(true);
      toast.success(res.message || "Welcome inside.");
      setFirstName("");
      setEmail("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something interrupted the story. Try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="newsletter-success"
        className="border border-dh-gold/30 bg-dh-forest/20 px-6 py-8 text-center"
      >
        <p className="font-display text-2xl text-dh-cream">You’re inside.</p>
        <p className="mt-2 font-body text-sm text-dh-muted">
          You’ll be first to know. {home.community.footnote}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="newsletter-form" noValidate className="w-full">
      <div className={`grid gap-4 ${compact ? "sm:grid-cols-1" : "sm:grid-cols-2"}`}>
        <div>
          <label htmlFor="nl-first-name" className="sr-only">
            First Name
          </label>
          <input
            id="nl-first-name"
            data-testid="newsletter-first-name"
            type="text"
            autoComplete="given-name"
            placeholder="First Name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className="w-full border-b border-dh-cream/20 bg-transparent px-1 py-3 font-body text-base text-dh-cream placeholder:text-dh-muted/70 outline-none transition-colors duration-300 focus:border-dh-gold"
          />
        </div>
        <div>
          <label htmlFor="nl-email" className="sr-only">
            Email Address
          </label>
          <input
            id="nl-email"
            data-testid="newsletter-email"
            type="email"
            autoComplete="email"
            placeholder="Email Address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full border-b border-dh-cream/20 bg-transparent px-1 py-3 font-body text-base text-dh-cream placeholder:text-dh-muted/70 outline-none transition-colors duration-300 focus:border-dh-gold"
          />
        </div>
      </div>

      <button
        type="submit"
        data-testid="newsletter-submit"
        disabled={loading}
        className="group mt-8 inline-flex items-center justify-center gap-3 border border-dh-gold/70 bg-transparent px-8 py-4 font-body text-[11px] uppercase tracking-[0.22em] text-dh-gold transition-[background-color,color] duration-500 hover:bg-dh-gold hover:text-dh-bg2 disabled:opacity-50"
      >
        {loading ? "Sending…" : home.community.cta}
        <ArrowRight size={15} className="transition-transform duration-500 group-hover:translate-x-1" />
      </button>
    </form>
  );
}
